import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, can } from '../../lib/api';
import { useConfirm } from '../../lib/Modal';
import { Banner, Button, Card, LoadError, Skeleton, input } from '../../lib/ui';
import { Hilfe } from '../help/Help';
import { t } from '../../lib/i18n';

/**
 * Which hosts may load this banner, and which of them are only for testing.
 *
 * A test domain is the answer to a question every customer eventually asks and every
 * competitor answers with "filter it out of your reports afterwards": how do I try this on
 * staging without ruining my numbers? Filtering later is the wrong shape, because the
 * filter has to be remembered and the flag does not.
 */
interface DomainRow {
  id: string;
  domain: string;
  is_test: boolean;
  primary: boolean;
  verified: boolean;
}

export function DomainsCard({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const [domain, setDomain] = useState('');
  const [isTest, setIsTest] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmNode, confirm] = useConfirm();

  const domains = useQuery({
    queryKey: ['domains', siteId],
    queryFn: () => api<{ data: DomainRow[] }>(`/v1/sites/${siteId}/domains`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['domains', siteId] });

  const add = useMutation({
    mutationFn: () => api(`/v1/sites/${siteId}/domains`, {
      method: 'POST',
      body: { domain, is_test: isTest },
    }),
    onSuccess: () => { setDomain(''); setProblem(null); void refresh(); },
    onError: (e) => setProblem(
      e instanceof ApiError && e.problem.errors?.some((x) => x.code === 'taken')
        ? t('Diese Domain gehört bereits zu einer Website.')
        : t('Die Domain sieht nicht richtig aus — nur der Host, ohne https:// und ohne Pfad.'),
    ),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/sites/${siteId}/domains/${id}`, { method: 'DELETE' }),
    onSuccess: () => { setProblem(null); void refresh(); },
    onError: () => setProblem(t('Die Domain konnte nicht entfernt werden.')),
  });
  // The site itself is invalidated too: the primary domain is what the scan panel, the
  // install check and the setup checklist print, and they read it from the site, not from
  // this list.
  const makePrimary = useMutation({
    mutationFn: (id: string) => api(`/v1/sites/${siteId}/domains/${id}/primary`, { method: 'PUT' }),
    onSuccess: () => {
      setProblem(null);
      void refresh();
      void qc.invalidateQueries({ queryKey: ['site', siteId] });
    },
    onError: () => setProblem(t('Die Hauptdomain konnte nicht gewechselt werden.')),
  });

  const writable = can('site:update');
  const rows = domains.data?.data ?? [];

  return (
    <Card title={t('Domains')} actions={<Hilfe to="domains" />}>
      {confirmNode}
      <p className="mb-3 text-xs text-[var(--color-muted)]">
        {t('Nur diese Hosts dürfen das Banner laden und Ereignisse melden. Als Testumgebung markierte Domains zeigen das Banner mit einem Hinweis und melden nichts — die Auswertung bleibt sauber.')}
      </p>
      <p className="mb-3 text-xs text-[var(--color-muted)]">
        {t('Die Hauptdomain ist die Adresse dieser Website: Scans starten dort, der Einbau-Test prüft dort, und die Cookie-Erklärung nennt sie. Eine Testumgebung kann sie nicht sein — von dort kommen keine Ereignisse.')}
      </p>

      {domains.isPending && <Skeleton rows={2} />}
      {domains.isError && <LoadError what={t('Die Domains')} onRetry={() => void domains.refetch()} />}
      {problem && <div className="mb-3"><Banner kind="error">{problem}</Banner></div>}

      {rows.length > 0 && (
        <ul className="mb-4 divide-y divide-[var(--color-line)]">
          {rows.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-[13px]">
              <span className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono">{d.domain}</span>
                {d.primary && (
                  <span className="text-xs text-[var(--color-muted)]">{t('Hauptdomain')}</span>
                )}
                {d.is_test && (
                  <span className="rounded-[2px] bg-[var(--color-note)] px-1.5 py-0.5 text-[11px] text-[var(--color-note-ink)]">
                    {t('Testumgebung')}
                  </span>
                )}
              </span>
              {writable && !d.primary && (
                <span className="flex shrink-0 items-center gap-3">
                  {/* No confirmation: this is reversible by pressing the same button on the
                      old domain, and a dialog in front of a reversible action is how the
                      irreversible ones get clicked through unread. */}
                  {d.verified && !d.is_test && (
                    <button className="text-xs text-[var(--color-muted)] hover:underline"
                      disabled={makePrimary.isPending}
                      onClick={() => makePrimary.mutate(d.id)}>
                      {t('Als Hauptdomain festlegen')}
                    </button>
                  )}
                  <button className="text-xs text-[var(--color-danger)] hover:underline"
                    onClick={() => confirm({
                      title: t('Domain entfernen?'),
                      danger: true,
                      confirmLabel: t('Entfernen'),
                      body: <>{t('Auf {domain} lädt das Banner danach nicht mehr, und Ereignisse von dort werden abgewiesen.', { domain: d.domain })}</>,
                      onConfirm: () => remove.mutate(d.id),
                    })}>
                    {t('Entfernen')}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {writable && (
        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--color-line)] pt-4">
          <input className={`${input} min-w-[200px] flex-1`} value={domain}
            placeholder="staging.kunde.de" aria-label={t('Domain')}
            onChange={(e) => setDomain(e.target.value)} />
          <label className="flex items-center gap-1.5 text-[13px]">
            <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} />
            {t('Testumgebung')}
          </label>
          <Button variant="secondary" disabled={!domain.trim() || add.isPending}
            onClick={() => add.mutate()}>
            {t('Hinzufügen')}
          </Button>
        </div>
      )}
    </Card>
  );
}
